import { VenueId } from '../../venues/venues.types';
import { VENUES } from '../../venues/venues.config';
import { USER_ROLES, PRACTICE_OWNER_TYPES } from '../../utils/constants';
import { TimelineParty } from '../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { isCharityAffiliatedMember } from '../../utils/memberRoles';
import {
	AccessLevel,
	SubscriptionTier,
	VenueAccessMatrix,
	VenueAccessByTier,
} from './venue-access-matrix.types';

const PRACTICE_OWNER_VENUE_ACCESS: Record<number, VenueAccessByTier> = {
	[PRACTICE_OWNER_TYPES.INDEPENDENT]: {
		[SubscriptionTier.Free]: {
			[VenueId.Grow]: AccessLevel.Full,
			[VenueId.Ally]: AccessLevel.Full,
			[VenueId.Connect]: AccessLevel.Full,
			[VenueId.Share]: AccessLevel.Full,
		},
		[SubscriptionTier.Paid]: {
			[VenueId.Grow]: AccessLevel.Full,
			[VenueId.Ally]: AccessLevel.Full,
			[VenueId.Connect]: AccessLevel.Full,
			[VenueId.Share]: AccessLevel.Full,
		},
	},
	[PRACTICE_OWNER_TYPES.ASPIRING]: {
		[SubscriptionTier.Free]: {
			[VenueId.Grow]: AccessLevel.Subset,
			[VenueId.Ally]: AccessLevel.Full,
			[VenueId.Share]: AccessLevel.Full,
		},
		[SubscriptionTier.Paid]: {
			[VenueId.Grow]: AccessLevel.Subset,
			[VenueId.Ally]: AccessLevel.Full,
			[VenueId.Share]: AccessLevel.Full,
		},
	},
	[PRACTICE_OWNER_TYPES.FORMER]: {
		[SubscriptionTier.Free]: {
			[VenueId.Grow]: AccessLevel.Full,
			[VenueId.Ally]: AccessLevel.Full,
			[VenueId.Share]: AccessLevel.Full,
		},
		[SubscriptionTier.Paid]: {
			[VenueId.Grow]: AccessLevel.Full,
			[VenueId.Ally]: AccessLevel.Full,
			[VenueId.Share]: AccessLevel.Full,
		},
	},
	[PRACTICE_OWNER_TYPES.CORPORATE]: {
		[SubscriptionTier.Free]: {
			[VenueId.Grow]: AccessLevel.Full,
			[VenueId.Ally]: AccessLevel.Full,
			[VenueId.Share]: AccessLevel.Full,
		},
		[SubscriptionTier.Paid]: {
			[VenueId.Grow]: AccessLevel.Full,
			[VenueId.Ally]: AccessLevel.Full,
			[VenueId.Share]: AccessLevel.Full,
		},
	},
};

export const VENUE_ACCESS: VenueAccessMatrix = {
	[USER_ROLES.CHARITY_ADMIN]: {
		[SubscriptionTier.Free]: {
			[VenueId.Share]: AccessLevel.Full,
		},
		[SubscriptionTier.Paid]: {
			[VenueId.Share]: AccessLevel.Full,
		},
	},
	[USER_ROLES.REFERRAL_USER]: {
		[SubscriptionTier.Free]: {
			[VenueId.Share]: AccessLevel.Full,
		},
		[SubscriptionTier.Paid]: {
			[VenueId.Share]: AccessLevel.Full,
		},
	},
	[USER_ROLES.USER]: {
		[SubscriptionTier.Free]: {
			[VenueId.Grow]: AccessLevel.Full,
			[VenueId.Ally]: AccessLevel.Full,
			[VenueId.Share]: AccessLevel.Full,
		},
		[SubscriptionTier.Paid]: {
			[VenueId.Grow]: AccessLevel.Full,
			[VenueId.Ally]: AccessLevel.Full,
			[VenueId.Share]: AccessLevel.Full,
		},
	},
	[USER_ROLES.PRACTICE_SECONDARY_USER]: {
		[SubscriptionTier.Free]: {
			[VenueId.Grow]: AccessLevel.Full,
			[VenueId.Ally]: AccessLevel.Full,
			[VenueId.Share]: AccessLevel.Full,
		},
		[SubscriptionTier.Paid]: {
			[VenueId.Grow]: AccessLevel.Full,
			[VenueId.Ally]: AccessLevel.Full,
			[VenueId.Share]: AccessLevel.Full,
		},
	},
};

const hasBrokerOrObserverEngagement = (user: any): boolean =>
	(user?.engagements ?? []).some(
		(e: any) =>
			e.party === TimelineParty.Broker ||
			e.party === TimelineParty.Observer
	);

const isBrokerConnectEnabled = (user: any): boolean =>
	!!user?.isMarketplaceBroker && hasBrokerOrObserverEngagement(user);

export const getVenueAccessLevel = (user: any, venueId: VenueId): AccessLevel => {
	if (!user) return AccessLevel.None;
	if (user?.roleId === USER_ROLES.ADMIN) return AccessLevel.None;

	if (venueId === VenueId.Ally) return AccessLevel.None;

	if (isCharityAffiliatedMember(user)) {
		return venueId === VenueId.Share ? AccessLevel.Full : AccessLevel.None;
	}

	if (user.roleId === USER_ROLES.PRACTICE_SECONDARY_USER && user.isMarketplaceBuyer && venueId === VenueId.Grow) {
		return AccessLevel.None;
	}

	if (venueId === VenueId.Connect && user?.roleId === USER_ROLES.PRACTICE_SECONDARY_USER) {
		if (user.isMarketplaceBuyer) return AccessLevel.Full;
		return (user.engagements ?? []).some(
			(e: any) => e.party === TimelineParty.Seller
		)
			? AccessLevel.Full
			: AccessLevel.None;
	}

	if (user.roleId === USER_ROLES.PRACTICE_OWNER) {
		const ownerType = user.practiceOwnerType ?? PRACTICE_OWNER_TYPES.INDEPENDENT;
		const tier = user.hasPaidSubscription ? SubscriptionTier.Paid : SubscriptionTier.Free;
		const row = PRACTICE_OWNER_VENUE_ACCESS[ownerType]?.[tier] ?? {};
		return row[venueId] ?? AccessLevel.None;
	}

	if (user.isMarketplaceBroker) {
		if (venueId === VenueId.Share) return AccessLevel.Full;
		if (venueId === VenueId.Connect) {
			return isBrokerConnectEnabled(user) ? AccessLevel.Full : AccessLevel.None;
		}
		return AccessLevel.None;
	}

	if (user.roleId === USER_ROLES.USER && user.isMarketplaceBuyer) {
		return venueId === VenueId.Connect || venueId === VenueId.Share
			? AccessLevel.Full
			: AccessLevel.None;
	}

	const tier = user.hasPaidSubscription ? SubscriptionTier.Paid : SubscriptionTier.Free;
	const row = VENUE_ACCESS[user.roleId]?.[tier];
	if (!row) return AccessLevel.None;
	return row[venueId] ?? AccessLevel.None;
};

export const canAccessVenue = (user: any, venueId: VenueId): boolean =>
	getVenueAccessLevel(user, venueId) !== AccessLevel.None;

export const getAccessibleVenues = (user: any): VenueId[] => {
	if (!user) return [];
	return VENUES.filter((v) => canAccessVenue(user, v.id)).map((v) => v.id);
};

export type VenueAvailability = 'enabled' | 'disabled' | 'hidden';

export const getVenueAvailability = (
	user: any,
	venueId: VenueId
): VenueAvailability => {
	if (canAccessVenue(user, venueId)) {
		return 'enabled';
	}

	if (user?.isMarketplaceBroker && venueId === VenueId.Connect) {
		return 'disabled';
	}
	return 'hidden';
};
